import React, { useState, useRef, useEffect } from "react";
import { Row, Col } from "react-bootstrap";
import { CommonSelect, CommonInput } from "../../Input";

const getExchangeRate = (from, to) => {
  if (!from || !to) return 0;
  const rates = {
    SGD_MYR: 3.4225,
    USD_MYR: 4.7085,
    SGD_PHP: 41.52,
    USD_PHP: 56.12,
  };
  return rates[`${from}_${to}`] || 1.0;
};

const ReceiverCard = ({
  receiver,
  sendCurrency,
  onRemoveReceiver,
  onUpdateReceiver,
}) => {
  const [sendAmount, setSendAmount] = useState("");
  const fileInputRef = useRef(null);
  const [files, setFiles] = useState([]);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);

    // Only allow up to 5 files total
    if (files.length + selectedFiles.length > 5) {
      alert("You can only upload up to 5 files.");
      return;
    }

    const newFiles = selectedFiles.map((file) => {
      let previewUrl = null;
      if (file.type.startsWith("image/")) {
        previewUrl = URL.createObjectURL(file);
      }
      return { file, previewUrl };
    });

    setFiles([...files, ...newFiles]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleDeleteFile = (e, index) => {
    e.stopPropagation();
    const newFiles = [...files];
    const removedFile = newFiles.splice(index, 1)[0];
    if (removedFile.previewUrl) {
      URL.revokeObjectURL(removedFile.previewUrl);
    }
    setFiles(newFiles);
  };

  const currentRate = getExchangeRate(
    sendCurrency ? sendCurrency.value : null,
    receiver.currency || "MYR",
  );

  const receivingAmount =
    sendAmount && !isNaN(sendAmount)
      ? (parseFloat(sendAmount) * currentRate).toFixed(4)
      : "0.00";

  useEffect(() => {
    if (onUpdateReceiver) {
      onUpdateReceiver(receiver.id, {
        sendAmount: sendAmount || "0",
        receivingAmount,
        fee: "0", // Hardcoded per requirements initially, can update later
        sendCurrency: sendCurrency?.value || "",
        receivingCurrency: receiver.currency || "MYR",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendAmount, receivingAmount, sendCurrency, receiver.id]);

  return (
    <div
      className="card border-0 shadow-sm rounded-4 mb-0 bg-white hover-shadow"
      style={{ transition: "all 0.3s ease" }}
    >
      <div className="card-body p-4">
        <div className="d-flex align-items-stretch gap-4">
          {/* Receiver Info Box */}
          <div
            className="bg-light rounded-3 px-3 d-flex flex-column justify-content-center"
            style={{
              minWidth: "200px",
              border: "1px solid #e0e0e0",
            }}
          >
            <div className="text-muted fs-11 lh-1 mb-2 text-center">
              Receiver
            </div>
            <div
              className="fw-bold text-black fs-13 lh-1 text-truncate text-center w-100"
              title={receiver.name}
            >
              {receiver.name}
            </div>
          </div>

          {/* Amount and Purpose Controls */}
          <div className="flex-grow-1">
            <Row className="g-3 align-items-start">
              <Col xl={3} md={6}>
                <div>
                  <CommonInput
                    label="Send Amount"
                    placeholder="0.00"
                    prefix={sendCurrency ? sendCurrency.value : ""}
                    readOnly={!sendCurrency}
                    value={sendAmount}
                    onChange={(e) => setSendAmount(e.target.value)}
                  />
                  {sendCurrency && (
                    <span className="text-muted fs-14 d-block mt-1 ps-1">
                      1 {sendCurrency.value} = {currentRate}{" "}
                      {receiver.currency || "MYR"}
                    </span>
                  )}
                </div>
              </Col>
              <Col xl={3} md={6}>
                <CommonInput
                  label="Receiving Amount"
                  placeholder="0.00"
                  value={receivingAmount}
                  prefix={receiver.currency || "MYR"}
                  readOnly
                />
              </Col>
              <Col xl={2} md={6}>
                <CommonInput label="Fee" value="0" readOnly />
              </Col>
              <Col xl={4} md={6}>
                <CommonSelect
                  label="Purpose of Transfer"
                  placeholder="Select purpose"
                  defaultValue={{
                    value: "payment",
                    label: "Supplier/vendor payment",
                  }}
                  options={[
                    { value: "payment", label: "Supplier/vendor payment" },
                    { value: "payroll", label: "Payroll" },
                    { value: "family", label: "Family expenses" },
                  ]}
                />
              </Col>
            </Row>
          </div>

          {/* Remove Action */}
          <button
            className="btn btn-light rounded-circle p-0 d-flex align-items-center justify-content-center border-0 mt-4 align-self-start"
            style={{
              width: "40px",
              height: "40px",
              minWidth: "40px",
              backgroundColor: "#FFF1F2",
              color: "#E11D48",
              transition: "all 0.2s",
            }}
            onClick={() => onRemoveReceiver(receiver.id)}
            title="Remove receiver"
          >
            <i className="fa fa-times fs-16"></i>
          </button>
        </div>

        {/* Invoice Upload Section */}
        {Number(sendAmount) > 0 && (
          <div className="mt-4 pt-4 border-top">
            <h6 className="fw-bold text-black mb-3 fs-14">Invoice</h6>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: "none" }}
              accept=".pdf,.jpg,.jpeg,.png,.docx"
              multiple
              onChange={handleFileChange}
            />
            {/* Display list of uploaded files */}
            <div className="d-flex flex-wrap gap-3 mb-3">
              {files.map((item, idx) => (
                <div
                  key={idx}
                  className="bg-white rounded-3 d-flex align-items-center py-2 px-3 border border-2 position-relative"
                  style={{
                    borderColor: "var(--primary)",
                    backgroundColor: "rgba(13, 110, 253, 0.05)",
                    width: "fit-content",
                    minWidth: "250px",
                  }}
                >
                  <div className="d-flex align-items-center gap-3 w-100">
                    {item.previewUrl ? (
                      <img
                        src={item.previewUrl}
                        alt="preview"
                        style={{
                          width: "35px",
                          height: "35px",
                          objectFit: "cover",
                          borderRadius: "6px",
                          border: "1px solid #eee",
                        }}
                      />
                    ) : (
                      <i
                        className={"fas fa-file-invoice text-primary"}
                        style={{
                          fontSize: "24px",
                        }}
                      ></i>
                    )}
                    <div className="flex-grow-1">
                      <div
                        className="fw-bold text-black fs-13 text-truncate"
                        style={{ maxWidth: "150px", lineHeight: "1.2" }}
                      >
                        {item.file.name}
                      </div>
                    </div>
                    <div
                      className="p-1 px-2 hover-bg-light rounded cursor-pointer"
                      onClick={(e) => handleDeleteFile(e, idx)}
                      title="Remove file"
                    >
                      <i className="fas fa-trash-alt text-danger fs-14"></i>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Upload Trigger - hide if 5 files reached */}
            {files.length < 5 && (
              <div
                className="bg-white rounded-3 d-flex align-items-center py-2 px-3 cursor-pointer border border-2 border-dashed position-relative"
                style={{
                  borderColor: "#e0e0e0",
                  backgroundColor: "#fafafa",
                  width: "fit-content",
                  minWidth: "300px",
                }}
                onClick={() => fileInputRef.current.click()}
              >
                <div className="d-flex align-items-center gap-3 w-100">
                  <i
                    className="fas fa-cloud-upload-alt"
                    style={{
                      fontSize: "24px",
                      color: "#adb5bd",
                    }}
                  ></i>
                  <div className="flex-grow-1">
                    <div
                      className="fw-bold text-black fs-13 text-truncate"
                      style={{ maxWidth: "200px", lineHeight: "1.2" }}
                    >
                      Click to upload file ({files.length}/5)
                    </div>
                    <div className="text-muted fs-11 mt-1">
                      pdf, jpg, png, docx &lt; 5MB
                    </div>
                  </div>
                </div>
              </div>
            )}
            <style>{`
              .border-dashed {
                border-style: dashed !important;
              }
              .hover-bg-light:hover {
                background-color: rgba(220, 53, 69, 0.1);
              }
            `}</style>
          </div>
        )}
      </div>
    </div>
  );
};

const Step2BulkPaymentInfo = ({
  selectedReceivers,
  onRemoveReceiver,
  onUpdateReceiver,
}) => {
  const [sendCurrency, setSendCurrency] = useState(null);
  return (
    <div className="mt-2">
      {/* Top Configuration Bar */}
      <Row className="mb-4 g-3 align-items-end">
        <Col md={3}>
          <CommonSelect
            label="Send Currency"
            placeholder="Select send currency"
            value={sendCurrency}
            onChange={(val) => setSendCurrency(val)}
            options={[
              { value: "SGD", label: "Singapore Dollar" },
              { value: "USD", label: "US Dollar" },
            ]}
          />
        </Col>
        <Col md={3}>
          <CommonSelect
            label="Source of Funds"
            placeholder="Select source of funds"
            options={[
              { value: "salary", label: "Salary" },
              { value: "savings", label: "Savings" },
              { value: "business", label: "Business" },
              { value: "loan", label: "Loan" },
            ]}
          />
        </Col>
        <Col md={6}>
          <CommonInput
            label="Transaction Reference"
            placeholder="Enter transaction reference"
          />
        </Col>
      </Row>

      {/* Receiver List */}
      <div className="d-flex flex-column gap-3">
        {selectedReceivers.map((receiver) => (
          <ReceiverCard
            key={receiver.id}
            receiver={receiver}
            sendCurrency={sendCurrency}
            onRemoveReceiver={onRemoveReceiver}
            onUpdateReceiver={onUpdateReceiver}
          />
        ))}
      </div>
    </div>
  );
};

export default Step2BulkPaymentInfo;
